// features/dashboard/CashierDashboard.tsx
/**
 * Dedicated Cashier Dashboard — EtiPOS Operational Station
 * 
 * Exclusively provides the cashier with their personal shift stats, cash in drawer,
 * personal sales velocity, assigned terminal details, and fast POS launch.
 * Executive business metrics (profit margins, cross-branch data, total company revenue)
 * are strictly hidden to ensure cashier confidentiality and role isolation.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import apiClient from '@/lib/api-client';
import { getActiveShift, openShift } from '@/features/pos/services/posApi';
import { ShiftModal } from '@/features/pos/components/ShiftModal';
import { ReceiptModal } from '@/features/pos/components/ReceiptModal';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
    ShoppingCart,
    CreditCard,
    Banknote,
    Smartphone,
    RefreshCw,
    Clock,
    Sparkles,
    CheckCircle2,
    AlertCircle,
    Store,
    Receipt,
    ArrowUpRight,
    Wallet,
    DollarSign,
    UserCheck,
    ChevronRight,
    TrendingUp,
    Shield,
} from 'lucide-react';
import { clsx } from 'clsx';

// ============================================================
// HELPERS
// ============================================================

const formatNaira = (val: string | number | null | undefined): string => {
    const num = typeof val === 'string' ? parseFloat(val || '0') : (val ?? 0);
    if (isNaN(num)) return '₦0.00';
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCompactNaira = (val: string | number | null | undefined): string => {
    const num = typeof val === 'string' ? parseFloat(val || '0') : (val ?? 0);
    if (isNaN(num)) return '₦0.00';
    if (num >= 1_000_000) return `₦${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `₦${(num / 1_000).toFixed(1)}K`;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
};

export function CashierDashboard() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId) || (user as any)?.branchId;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Shift modal & receipt state
    const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
    const [openingCashInput, setOpeningCashInput] = useState('0');
    const [selectedReceiptSale, setSelectedReceiptSale] = useState<{ id: string; total: number } | null>(null);

    // Active shift query (scoped to logged-in cashier)
    const {
        data: activeShift,
        isLoading: isShiftLoading,
        refetch: refetchShift,
        isFetching: isShiftFetching,
    } = useQuery({
        queryKey: ['cashier-active-shift', tenantId, user?.id],
        queryFn: () => getActiveShift(tenantId, 'retail'),
        enabled: !!tenantId,
        refetchInterval: 30000, // Refresh every 30s
    });

    // Cashier's recent completed sales query (scoped to cashierId)
    const {
        data: salesData,
        isLoading: isSalesLoading,
        refetch: refetchSales,
    } = useQuery({
        queryKey: ['cashier-recent-sales', tenantId, user?.id, activeShift?.id],
        queryFn: async () => {
            const params: Record<string, any> = {
                cashierId: user?.id,
                limit: 10,
                sortBy: 'createdAt',
                order: 'desc',
            };
            if (activeShift?.id) {
                params.shiftId = activeShift.id;
            }
            const res = await apiClient.get(`/tenants/${tenantId}/sales`, { params });
            return res.data?.data || res.data?.sales || [];
        },
        enabled: !!tenantId && !!user?.id,
    });

    // Open shift mutation
    const openShiftMutation = useMutation({
        mutationFn: async (cash: string) => {
            return openShift(tenantId, selectedBranchId || undefined, cash, 'retail');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cashier-active-shift'] });
            queryClient.invalidateQueries({ queryKey: ['cashier-recent-sales'] });
            setIsShiftModalOpen(false);
            addToast({
                type: 'success',
                title: 'Shift Opened',
                message: 'Your cashier shift is active. You can now process transactions.',
            });
        },
        onError: (err: any) => {
            const errMsg = err.response?.data?.message || err.message || '';
            const errCode = err.response?.data?.error?.code || err?.code || '';
            const isAlreadyOpen =
                errCode === 'SHIFT_ALREADY_OPEN' ||
                errMsg.toLowerCase().includes('already have an open') ||
                errMsg.toLowerCase().includes('shift_already_open');

            if (isAlreadyOpen) {
                queryClient.invalidateQueries({ queryKey: ['cashier-active-shift'] });
                queryClient.invalidateQueries({ queryKey: ['active-shift'] });
                setIsShiftModalOpen(false);
                addToast({
                    type: 'info',
                    title: 'Active Shift Found',
                    message: 'You already have an open shift. Opening POS terminal directly.',
                });
                navigate('/pos');
                return;
            }

            addToast({
                type: 'error',
                title: 'Failed to Open Shift',
                message: errMsg || 'Error opening shift',
            });
        },
    });

    // Computations from active shift & sales
    const metrics = useMemo(() => {
        const totalSales = parseFloat(activeShift?.totalSales || '0');
        const openingCash = parseFloat(activeShift?.openingCash || '0');
        const cashSales = parseFloat(activeShift?.totalCashSales || '0');
        const cardSales = parseFloat(activeShift?.totalCardSales || '0');
        const transferSales = parseFloat(activeShift?.totalTransferSales || '0');
        const momoSales = parseFloat(activeShift?.totalMomoSales || '0');
        const txCount = activeShift?.transactionCount || salesData?.length || 0;
        const avgBasket = txCount > 0 ? totalSales / txCount : 0;
        const cashInDrawer = openingCash + cashSales;

        return {
            totalSales,
            openingCash,
            cashSales,
            cardSales,
            transferSales,
            momoSales,
            txCount,
            avgBasket,
            cashInDrawer,
        };
    }, [activeShift, salesData]);

    const handleRefresh = () => {
        refetchShift();
        refetchSales();
    };

    const hasShift = !!activeShift && activeShift.status === 'open';

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
                {/* ==================== CASHIER WELCOME HEADER ==================== */}
                <div className="bg-gradient-to-r from-teal-900 via-slate-900 to-teal-950 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden border border-teal-800/40">
                    <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-radial from-teal-500/10 via-transparent to-transparent pointer-events-none" />

                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
                        <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/20 text-teal-300 border border-teal-400/30">
                                    <UserCheck size={13} />
                                    Cashier Station
                                </span>
                                {activeShift?.branchName && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-800/80 text-slate-300 border border-slate-700">
                                        <Store size={13} className="text-teal-400" />
                                        {activeShift.branchName}
                                    </span>
                                )}
                                <span
                                    className={clsx(
                                        'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
                                        hasShift
                                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30'
                                            : 'bg-amber-500/20 text-amber-300 border-amber-400/30'
                                    )}
                                >
                                    <span className={clsx('w-2 h-2 rounded-full', hasShift ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400')} />
                                    {hasShift ? 'Shift Active' : 'No Active Shift'}
                                </span>
                            </div>

                            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                Welcome, {user?.firstName || 'Cashier'} 👋
                            </h1>
                            <p className="text-slate-300 text-sm max-w-xl">
                                Ready to serve customers. Your drawer, personal metrics, and transaction register are live.
                            </p>
                        </div>

                        {/* Fast Action Buttons */}
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={handleRefresh}
                                disabled={isShiftFetching}
                                className="p-3 bg-white/10 hover:bg-white/15 text-white rounded-2xl transition border border-white/10"
                                title="Refresh Shift Stats"
                                type="button"
                            >
                                <RefreshCw size={18} className={clsx(isShiftFetching && 'animate-spin')} />
                            </button>

                            {hasShift ? (
                                <button
                                    onClick={() => navigate('/pos')}
                                    className="flex items-center gap-2.5 px-6 py-3.5 bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-300 hover:to-emerald-300 text-slate-950 font-bold rounded-2xl shadow-lg shadow-teal-500/25 transition transform active:scale-95"
                                    type="button"
                                >
                                    <ShoppingCart size={18} className="stroke-[2.5]" />
                                    Launch POS Terminal
                                    <ChevronRight size={18} />
                                </button>
                            ) : (
                                <button
                                    onClick={() => setIsShiftModalOpen(true)}
                                    className="flex items-center gap-2.5 px-6 py-3.5 bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-300 hover:to-orange-300 text-slate-950 font-bold rounded-2xl shadow-lg shadow-amber-500/25 transition transform active:scale-95"
                                    type="button"
                                >
                                    <Clock size={18} className="stroke-[2.5]" />
                                    Start Cashier Shift
                                    <ChevronRight size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ==================== 4 METRICS CARDS (Cashier Scope Only) ==================== */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Shift Sales */}
                    <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm relative overflow-hidden group hover:border-teal-300 transition-all">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Shift Total Sales</span>
                            <div className="p-2.5 rounded-xl bg-teal-50 text-teal-600">
                                <DollarSign size={20} />
                            </div>
                        </div>
                        <p className="text-2xl font-black text-slate-900 tracking-tight">{formatCompactNaira(metrics.totalSales)}</p>
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <TrendingUp size={13} className="text-teal-600" />
                            {metrics.txCount} completed sale{metrics.txCount === 1 ? '' : 's'}
                        </p>
                    </div>

                    {/* Cash in Drawer */}
                    <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm relative overflow-hidden group hover:border-emerald-300 transition-all">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Cash in Drawer</span>
                            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
                                <Wallet size={20} />
                            </div>
                        </div>
                        <p className="text-2xl font-black text-slate-900 tracking-tight">{formatCompactNaira(metrics.cashInDrawer)}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            Opening {formatCompactNaira(metrics.openingCash)} + Collected {formatCompactNaira(metrics.cashSales)}
                        </p>
                    </div>

                    {/* Average Ticket */}
                    <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm relative overflow-hidden group hover:border-blue-300 transition-all">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Average Basket</span>
                            <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600">
                                <ShoppingCart size={20} />
                            </div>
                        </div>
                        <p className="text-2xl font-black text-slate-900 tracking-tight">{formatCompactNaira(metrics.avgBasket)}</p>
                        <p className="text-xs text-slate-500 mt-1">
                            Per transaction average
                        </p>
                    </div>

                    {/* Shift Duration */}
                    <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm relative overflow-hidden group hover:border-purple-300 transition-all">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Shift Started</span>
                            <div className="p-2.5 rounded-xl bg-purple-50 text-purple-600">
                                <Clock size={20} />
                            </div>
                        </div>
                        <p className="text-xl font-bold text-slate-900">
                            {activeShift?.openedAt ? new Date(activeShift.openedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                            {activeShift?.posTerminalName ? `Terminal: ${activeShift.posTerminalName}` : 'Standard Register'}
                        </p>
                    </div>
                </div>

                {/* ==================== PAYMENT CHANNELS BREAKDOWN ==================== */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm">
                    <h2 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <CreditCard size={16} className="text-teal-600" />
                        My Shift Payment Channels
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-100 flex items-center gap-3">
                            <div className="p-2.5 bg-emerald-100 text-emerald-700 rounded-lg">
                                <Banknote size={20} />
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">Cash</p>
                                <p className="text-base font-bold text-slate-900">{formatCompactNaira(metrics.cashSales)}</p>
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-blue-50/60 border border-blue-100 flex items-center gap-3">
                            <div className="p-2.5 bg-blue-100 text-blue-700 rounded-lg">
                                <CreditCard size={20} />
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide">POS Card</p>
                                <p className="text-base font-bold text-slate-900">{formatCompactNaira(metrics.cardSales)}</p>
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-purple-50/60 border border-purple-100 flex items-center gap-3">
                            <div className="p-2.5 bg-purple-100 text-purple-700 rounded-lg">
                                <Store size={20} />
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold text-purple-700 uppercase tracking-wide">Bank Transfer</p>
                                <p className="text-base font-bold text-slate-900">{formatCompactNaira(metrics.transferSales)}</p>
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-amber-50/60 border border-amber-100 flex items-center gap-3">
                            <div className="p-2.5 bg-amber-100 text-amber-700 rounded-lg">
                                <Smartphone size={20} />
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">Mobile / Loyalty</p>
                                <p className="text-base font-bold text-slate-900">{formatCompactNaira(metrics.momoSales)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ==================== RECENT TRANSACTIONS (CASHIER REGISTER) ==================== */}
                <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                        <div>
                            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                                <Receipt size={16} className="text-teal-600" />
                                My Shift Transactions
                            </h2>
                            <p className="text-xs text-slate-400 mt-0.5">Recent orders processed from your station</p>
                        </div>
                        {hasShift && (
                            <button
                                onClick={() => navigate('/pos')}
                                className="text-xs font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1"
                                type="button"
                            >
                                Open POS <ArrowUpRight size={13} />
                            </button>
                        )}
                    </div>

                    {isSalesLoading ? (
                        <div className="py-12 flex justify-center">
                            <Spinner size="lg" />
                        </div>
                    ) : !salesData || salesData.length === 0 ? (
                        <div className="py-12 text-center space-y-2">
                            <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                                <Receipt size={24} />
                            </div>
                            <p className="text-sm font-semibold text-slate-700">No transactions yet for this shift</p>
                            <p className="text-xs text-slate-400 max-w-sm mx-auto">
                                Open the POS terminal to scan products and ring up your first customer sale.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50/70 border-b border-slate-100 text-slate-500 font-semibold uppercase">
                                    <tr>
                                        <th className="py-3 px-4">Order #</th>
                                        <th className="py-3 px-4">Time</th>
                                        <th className="py-3 px-4">Customer</th>
                                        <th className="py-3 px-4">Items</th>
                                        <th className="py-3 px-4">Total</th>
                                        <th className="py-3 px-4">Status</th>
                                        <th className="py-3 px-4 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                                    {salesData.map((s: any) => (
                                        <tr key={s.id} className="hover:bg-slate-50/80 transition-colors">
                                            <td className="py-3 px-4 font-mono font-bold text-slate-900">{s.orderNumber}</td>
                                            <td className="py-3 px-4 text-slate-500">
                                                {s.createdAt ? new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="font-semibold text-slate-800">{s.customerName || 'Walk-in Customer'}</span>
                                                {s.customerPhone && <span className="block text-[11px] text-slate-400">{s.customerPhone}</span>}
                                            </td>
                                            <td className="py-3 px-4 text-slate-600">{s.items?.length || 1} items</td>
                                            <td className="py-3 px-4 font-bold text-slate-900">{formatNaira(s.grandTotal)}</td>
                                            <td className="py-3 px-4">
                                                <span
                                                    className={clsx(
                                                        'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold capitalize',
                                                        s.status === 'completed' && 'bg-emerald-50 text-emerald-700 border border-emerald-200',
                                                        s.status === 'voided' && 'bg-rose-50 text-rose-700 border border-rose-200',
                                                        s.status === 'refunded' && 'bg-amber-50 text-amber-700 border border-amber-200'
                                                    )}
                                                >
                                                    {s.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                <button
                                                    onClick={() => setSelectedReceiptSale({ id: s.id, total: parseFloat(s.grandTotal || '0') })}
                                                    className="px-2.5 py-1 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition border border-teal-200/60 inline-flex items-center gap-1"
                                                    type="button"
                                                >
                                                    <Receipt size={12} />
                                                    Receipt
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Shift Modal */}
            {isShiftModalOpen && (
                <ShiftModal
                    isOpen={isShiftModalOpen}
                    onClose={() => setIsShiftModalOpen(false)}
                    onConfirm={(cash) => openShiftMutation.mutate(cash)}
                    isLoading={openShiftMutation.isPending}
                    openingCash={openingCashInput}
                    onOpeningCashChange={setOpeningCashInput}
                />
            )}

            {/* Receipt Modal */}
            {selectedReceiptSale && (
                <ReceiptModal
                    isOpen={!!selectedReceiptSale}
                    onClose={() => setSelectedReceiptSale(null)}
                    onNewSale={() => setSelectedReceiptSale(null)}
                    tenantId={tenantId}
                    saleId={selectedReceiptSale.id}
                    total={selectedReceiptSale.total}
                />
            )}
        </div>
    );
}
